import { useEffect, useState } from "react";

import { Upload } from "lucide-react";

import { DOCUMENT_CLASSIFICATIONS } from "@/constants/documents";
import { useCaseByCaseNumber } from "@/hooks/queries/use-cases";

import { Button } from "@components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
} from "@components/ui/dialog";

import { useFiles } from "@hooks/queries/use-files";

import { UploadDocumentTypeItem } from "./UploadDocumentItem";
import { UploadDocumentTypeFilter } from "./UploadDocumentTypeFilter";

type UploadDocumentsModalProps = {
  caseNumber: string;
  onModalOpen?: () => void;
  onModalClose?: () => void;
};

export const UploadDocumentsModal = ({
  caseNumber,
  onModalOpen,
  onModalClose,
}: UploadDocumentsModalProps) => {
  const [selectedFilter, setSelectedFilter] = useState<string>("all");
  const [, setSelectedFiles] = useState<string[]>([]);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);

  const { data: files = [] } = useFiles({
    case_number: caseNumber,
  });
  const { data: _caseData } = useCaseByCaseNumber({
    case_number: caseNumber || "",
    enabled: !!caseNumber,
  });

  // Filter document types based on selection
  const filteredDocumentTypes =
    selectedFilter === "all"
      ? DOCUMENT_CLASSIFICATIONS
      : DOCUMENT_CLASSIFICATIONS.filter(
          (docType) => docType.category === selectedFilter
        );

  // Handle file selection change - commented out since we auto-select all files
  // const handleFileSelectionChange = (fileId: string, selected: boolean) => {
  //   if (selected) {
  //     setSelectedFiles((prev) => [...prev, fileId]);
  //   } else {
  //     setSelectedFiles((prev) => prev.filter((id) => id !== fileId));
  //   }
  // };

  // Handle modal open/close callbacks
  useEffect(() => {
    if (isUploadModalOpen) {
      onModalOpen?.();
    } else {
      onModalClose?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUploadModalOpen]);

  // Auto-select files when modal opens
  useEffect(() => {
    if (isUploadModalOpen && files.length > 0) {
      setSelectedFiles(files.map((file) => file.id));
    }
  }, [isUploadModalOpen, files]);

  // Handle Cancel button click
  const handleCancelClick = () => {
    setIsUploadModalOpen(false);
  };

  return (
    <>
      <Dialog open={isUploadModalOpen} onOpenChange={setIsUploadModalOpen}>
        <DialogTrigger asChild>
          <Button size="sm" className="space-x-2">
            <Upload />
            Upload case file
          </Button>
        </DialogTrigger>

        <DialogPortal>
          <DialogOverlay />
          <DialogContent className="flex max-h-[90vh] max-w-4xl flex-col p-0 md:min-w-[720px]">
            <div className="flex-shrink-0 px-6 pt-6">
              <DialogHeader>
                <DialogTitle className="text-xl font-semibold">
                  File Upload
                </DialogTitle>
              </DialogHeader>

              <div className="mt-4">
                <UploadDocumentTypeFilter
                  value={selectedFilter}
                  onValueChange={setSelectedFilter}
                />
              </div>

              {/* {selectedFiles.length > 0 && (
              <div className="mt-4 rounded-lg bg-blue-50 p-3">
                <p className="text-sm font-medium text-blue-900">
                  {selectedFiles.length} file
                  {selectedFiles.length === 1 ? "" : "s"} selected for export
                </p>
              </div>
            )} */}
            </div>

            <div className="flex-1 overflow-hidden px-6">
              <div className="h-full max-h-[60vh] space-y-4 overflow-y-auto rounded-lg">
                {filteredDocumentTypes.map((categoryItem) => (
                  <UploadDocumentTypeItem
                    key={categoryItem.id}
                    item={categoryItem}
                    files={
                      files.filter(
                        (file) => file.classification === categoryItem.category
                      ) || []
                    }
                    caseNumber={caseNumber}
                    // selectedFiles={selectedFiles}
                    // onFileSelectionChange={handleFileSelectionChange}
                  />
                ))}
              </div>
            </div>

            <div className="flex-shrink-0 px-6 pb-6">
              <DialogFooter className="gap-2">
                <Button
                  variant="simple-outline"
                  size="sm"
                  onClick={handleCancelClick}
                >
                  Return to Assessment Prep
                </Button>
              </DialogFooter>
            </div>
          </DialogContent>
        </DialogPortal>
      </Dialog>
    </>
  );
};
